import styled, { css } from 'styled-components'
import { Button as BaseButton } from '@base-ui/react/button'

import { tokens } from '@styles/tokens'

export type ColorType = 'neutral' | 'white' | 'primary' | 'success' | 'danger'

export type VariantType = 'solid' | 'outline' | 'ghost' | 'faded'

export type SizeString = 'small' | 'medium' | 'large' | 'xlarge'

export type SizeObject = { default: SizeString; mobile?: SizeString; tablet?: SizeString; desktop?: SizeString }
export type OptionalSizeObject = {
  default?: SizeString
  mobile?: SizeString
  tablet?: SizeString
  desktop?: SizeString
}

export type SizeType = SizeString | OptionalSizeObject

export const BorderColor: { [key in VariantType]: { [key in ColorType]: string } } = {
  solid: {
    neutral: 'var(--Border-Neutral, #BBC1D3)',
    white: 'var(--Persistent-White, #FFF)',
    primary: 'rgba(0, 0, 0, 0.2)',
    success: 'rgba(0, 0, 0, 0.2)',
    danger: 'rgba(0, 0, 0, 0.2)',
  },
  outline: {
    neutral: 'var(--Border-Neutral, #BBC1D3)',
    white: 'var(--Persistent-White, #FFF)',
    primary: 'var(--Foreground-Primary, #4D4BE7)',
    success: tokens.palette.success,
    danger: tokens.palette.danger,
  },
  ghost: {
    neutral: 'rgba(255,255,255,0)',
    white: 'rgba(255,255,255,0)',
    primary: 'rgba(255,255,255,0)',
    success: 'rgba(255,255,255,0)',
    danger: 'rgba(255,255,255,0)',
  },
  faded: {
    neutral: 'var(--Background-Neutral, #DFE2EA)',
    white: 'rgba(255, 255, 255, 0.28)',
    primary: 'var(--Background-Primary-faded, #EDECFD)',
    success: tokens.palette.successFaded,
    danger: tokens.palette.dangerFaded,
  },
}

export const BackgroundColor: { [key in VariantType]: { [key in ColorType]: string } } = {
  solid: {
    neutral: 'var(--Background-Neutral, #DFE2EA)',
    white: 'var(--Persistent-White, #FFF)',
    primary: tokens.palette.primaryStrong,
    success: tokens.palette.successVivid,
    danger: `linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%), ${tokens.palette.danger}`,
  },
  outline: {
    neutral: 'rgba(255,255,255,0)',
    white: 'rgba(255, 255, 255, 0)',
    primary: 'rgba(255, 255, 255, 0)',
    success: 'rgba(255, 255, 255, 0)',
    danger: 'rgba(255, 255, 255, 0)',
  },
  ghost: {
    neutral: 'rgba(255,255,255,0)',
    white: 'rgba(255,255,255,0)',
    primary: 'rgba(255,255,255,0)',
    success: 'rgba(255,255,255,0)',
    danger: 'rgba(255,255,255,0)',
  },
  faded: {
    neutral: 'var(--Background-Elevation-base, #FFF)',
    white: 'rgba(255, 255, 255, 0.28)',
    primary: 'var(--Background-Primary-faded, #EDECFD)',
    success: tokens.palette.successFaded,
    danger: tokens.palette.dangerFaded,
  },
}

export const Color: { [key in VariantType]: { [key in ColorType]: string } } = {
  solid: {
    neutral: 'var(--Foreground-Neutral-faded, #4D5874)',
    white: 'var(--Persistent-Black, #000)',
    primary: 'var(--On-background-Primary, #FFF)',
    success: 'var(--On-background-Primary, #FFF)',
    danger: 'var(--On-background-Primary, #FFF)',
  },
  outline: {
    neutral: 'var(--Foreground-Neutral-faded, #4D5874)',
    white: 'var(--Persistent-White, #FFF)',
    primary: 'var(--Foreground-Primary, var(--Foreground-Primary, #4D4BE7))',
    success: tokens.palette.success,
    danger: tokens.palette.danger,
  },
  ghost: {
    neutral: 'var(--Foreground-Neutral-faded, #4D5874)',
    white: 'var(--Persistent-White, #FFF)',
    primary: 'var(--Foreground-Primary, var(--Foreground-Primary, #4D4BE7))',
    success: tokens.palette.success,
    danger: tokens.palette.danger,
  },
  faded: {
    neutral: 'var(--Foreground-Neutral-faded, #4D5874)',
    white: 'var(--Persistent-Black, #000)',
    primary: 'var(--Foreground-Primary, var(--Foreground-Primary, #4D4BE7))',
    success: tokens.palette.success,
    danger: tokens.palette.danger,
  },
}

// small matches the 30px SearchBar/toolbar control height.
const HEIGHT: { [key in SizeString]: number } = {
  small: 30,
  medium: 36,
  large: 48,
  xlarge: 52,
}

const FONT_SIZE: { [key in SizeString]: number } = {
  small: 14,
  medium: 14,
  large: 16,
  xlarge: 18,
}

const getHoverCss = (variant: VariantType, color: ColorType) => {
  if (variant === 'solid' || variant === 'faded') {
    return css`
      transform: translateY(-1px);
      box-shadow: ${tokens.shadow.sm};
    `
  }
  const tint =
    color === 'primary'
      ? 'rgba(90, 88, 242, 0.08)'
      : color === 'success'
      ? 'rgba(5, 117, 31, 0.08)'
      : color === 'danger'
      ? 'rgba(203, 16, 29, 0.08)'
      : color === 'white'
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(20, 23, 31, 0.04)'
  return css`
    background: ${tint};
  `
}

export const HTMLButton = styled(BaseButton).withConfig({
  shouldForwardProp: (prop) => !['variant', 'color', 'size'].includes(prop),
})<{ variant: VariantType; color: ColorType; size: SizeObject }>`
  background-blend-mode: soft-light, normal;
  background: ${({ variant, color }) => BackgroundColor[variant][color]};
  outline: none;
  border: none;
  cursor: pointer;

  border-radius: ${tokens.radius.sm};
  border: 1px solid ${({ variant, color }) => BorderColor[variant][color]};

  transition: transform ${tokens.transition.fast}, box-shadow ${tokens.transition.fast},
    background ${tokens.transition.fast}, border-color ${tokens.transition.fast};

  &:hover:not(:disabled):not([data-disabled]) {
    ${({ variant, color }) => getHoverCss(variant, color)}
  }

  &:active:not(:disabled):not([data-disabled]) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.focusRing};
    border-color: ${tokens.palette.primaryStrong};
  }

  padding: ${({ variant, color }) =>
    color === 'primary' || (variant === 'solid' && color === 'success') ? '4px 12px' : '4px 8px'};
  box-sizing: border-box;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: ${({ variant, color }) => Color[variant][color]};

  overflow: hidden;

  position: relative;

  opacity: 1;

  &[data-disabled] {
    cursor: default;
    opacity: 0.6;
  }

  svg {
    path {
      fill: ${({ variant, color }) => Color[variant][color]};
    }
  }

  height: ${({ size }) => HEIGHT[size.desktop || size.default]}px;

  /* Body 3/Medium */
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ size }) => FONT_SIZE[size.desktop || size.default]}px;
  font-style: normal;
  font-weight: 400;

  @media (max-width: ${({ theme }) => theme.responsive.tablet.valueString}) {
    height: ${({ size }) => HEIGHT[size.tablet || size.default]}px;

    font-size: ${({ size }) => FONT_SIZE[size.tablet || size.default]}px;
  }

  @media (max-width: ${({ theme }) => theme.responsive.mobile.valueString}) {
    height: ${({ size }) => HEIGHT[size.mobile || size.default]}px;

    font-size: ${({ size }) => FONT_SIZE[size.mobile || size.default]}px;
  }
`

export const ChildrenContainer = styled.div<{ isLoading?: boolean }>`
  opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  line-height: 20px;
`

export const LoaderContainer = styled.div`
  position: absolute;

  cursor: default;

  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
`
